import { ref } from 'vue'
import apiClient from '@/services/api'
import type { FieldGroupEntity } from '@/composables/useCustomRoles'

/**
 * Il catalogo dei gruppi di campi, letto una volta dal backend.
 *
 * È condiviso a livello di modulo e non per componente: la pagina Ruoli e i
 * moduli di modifica hanno bisogno della stessa mappa, e il catalogo cambia solo
 * con un rilascio. Riscriverlo in TypeScript sarebbe ripetere l'errore delle due
 * mappe PERMISSIONS, che sono divergite in silenzio.
 */
const catalogue = ref<FieldGroupEntity[]>([])
let pending: Promise<void> | null = null

export function useFieldGroups() {
  async function loadFieldGroups() {
    if (catalogue.value?.length) return
    try {
      // Piu' componenti possono chiederlo insieme: una sola chiamata per tutti.
      pending ??= apiClient
        .get('/field-groups')
        .then(response => { catalogue.value = response.data?.entities ?? [] })
        .catch(() => { catalogue.value = [] })
        .finally(() => { pending = null })
      await pending
    } catch {
      // Anche una chiamata che fallisce subito non deve rompere il montaggio:
      // senza catalogo i campi restano quelli che il ruolo gia' apriva.
      pending = null
      catalogue.value = []
    }
  }

  return { catalogue, loadFieldGroups }
}

/** I campi di un gruppo. Vuoto se il catalogo non è ancora stato letto. */
export function fieldsOfGroup(entity: string, group: string): string[] {
  return catalogue.value
    .find(e => e.entity === entity)?.groups
    .find(g => g.group === group)?.fields ?? []
}

/** L'etichetta di un gruppo, per spiegare a video perché un campo è chiuso. */
export function groupLabelOfField(entity: string, field: string): string | null {
  const found = catalogue.value
    .find(e => e.entity === entity)?.groups
    .find(g => g.fields.includes(field))
  return found?.label ?? null
}
